import type { GoToNextStepOnClickOf } from '@unifyapps/defs/types/page/walkthrough';
import { useEffect } from 'react';

/**
 * Applies and restores pointer-events on the walkthrough target element so that
 * clicks are received (or blocked) according to goToNextStepOnClickOf and
 * enableInteractionWithTargetElement. Restores original value on cleanup.
 *
 * Note: This hook only handles the target element's own pointer-events.
 * The HighlightOverlay's blocking rect handles preventing hover events
 * from reaching parent elements (e.g. navbar item wrappers).
 */
export function useWalkthroughTargetPointerEvents(
  targetElement: HTMLElement | null,
  enableInteractionWithTargetElement: boolean | undefined,
  goToNextStepOnClickOf: GoToNextStepOnClickOf | undefined,
): void {
  useEffect(() => {
    if (!targetElement) return;

    const originalPointerEvents = targetElement.style.pointerEvents;

    if (goToNextStepOnClickOf === 'element') {
      // For element clicks, enable pointer events so target can receive clicks
      // The SVG hole allows clicks to pass through to the target
      targetElement.style.pointerEvents = 'auto';
    } else if (enableInteractionWithTargetElement === false) {
      targetElement.style.pointerEvents = 'none';
    } else {
      // Allow normal interaction
      targetElement.style.pointerEvents = 'auto';
    }

    return () => {
      if (targetElement) {
        targetElement.style.pointerEvents = originalPointerEvents || '';
      }
    };
  }, [targetElement, enableInteractionWithTargetElement, goToNextStepOnClickOf]);
}
